import { getCountryFlagUrl } from "@/lib/country-data";

// Crypto coin icon URLs (CoinGecko CDN — reliable public URLs).
// Shared between Hyperliquid HIP-4 outcome rendering and the scraped
// crypto sidebar map.
export const COIN_ICON_URLS: Record<string, string> = {
  btc: "https://assets.coingecko.com/coins/images/1/small/bitcoin.png",
  bitcoin: "https://assets.coingecko.com/coins/images/1/small/bitcoin.png",
  eth: "https://assets.coingecko.com/coins/images/279/small/ethereum.png",
  ethereum: "https://assets.coingecko.com/coins/images/279/small/ethereum.png",
  sol: "https://assets.coingecko.com/coins/images/4128/small/solana.png",
  solana: "https://assets.coingecko.com/coins/images/4128/small/solana.png",
  xrp: "https://assets.coingecko.com/coins/images/44/small/xrp-symbol-white-128.png",
  doge: "https://assets.coingecko.com/coins/images/5/small/dogecoin.png",
  dogecoin: "https://assets.coingecko.com/coins/images/5/small/dogecoin.png",
  bnb: "https://assets.coingecko.com/coins/images/825/small/bnb-icon2_2x.png",
  hype: "https://assets.coingecko.com/coins/images/50882/small/hyperliquid.jpg",
  microstrategy: "https://polymarket-upload.s3.us-east-2.amazonaws.com/will-microstrategy-purchase-bitcoin-july-1-7-mzoE5TYk_cCI.jpg",
};

// Macro / non-crypto topic images. HL ships none of these so we lift the
// curated graphic Polymarket uses for the same recurring event family
// (S3 is public). Add new entries here when a new economics/politics
// topic shows up; patterns are tested against the question slug + name
// + description so editorial slug tweaks can't hide a match.
//
// Polymarket reuses these images across the monthly/quarterly cadence
// (the filename says "january-…" but the URL is stable as they rotate
// the underlying question), so we don't have to swap them every month.
const HL_TOPIC_IMAGES: Array<{ pattern: RegExp; image: string }> = [
  {
    // CPI / inflation prints (monthly + annual variants).
    pattern: /\b(cpi|inflation|pce|core[-\s]?cpi)\b/i,
    image: 'https://polymarket-upload.s3.us-east-2.amazonaws.com/january-inflation-annual-vp5qkgK2R7Et.jpg',
  },
  {
    // Fed / FOMC rate decisions. HL question_name is the full resolution
    // blob, so we match the distinctive phrases inside it.
    pattern: /(\bfomc\b|federal funds|federal open market|fed funds rate|fed (rate|decision))/i,
    image: 'https://polymarket-upload.s3.us-east-2.amazonaws.com/jerome+powell+glasses1.png',
  },
  {
    // UEFA Champions League (winner / per-club markets).
    pattern: /(uefa|champions league)/i,
    image: 'https://polymarket-upload.s3.us-east-2.amazonaws.com/uefa-champions-league-2025-26-which-teams-qualify-StbSIjaEx2St.png',
  },
  {
    // NBA (Finals games, champion, playoffs, per-team series). HL ships these
    // as 2-way named-side markets with no image, so we use Polymarket's
    // generic basketball graphic (matchup-agnostic — works for every NBA
    // question without per-team logos). `\bnba\b` matches the slug
    // ("nba-finals-game-1-…") and the question_name ("2026 NBA Finals champion").
    pattern: /\bnba\b|nba finals|nba playoffs/i,
    image: 'https://polymarket-upload.s3.us-east-2.amazonaws.com/super+cool+basketball+in+red+and+blue+wow.png',
  },
  {
    // FIFA World Cup — match games ("World Cup: X vs Y") + the 48-way champion
    // futures. HL ships no artwork; mirror of backend image-resolver TOPIC_IMAGES.
    pattern: /\bworld cup\b/i,
    image: 'https://polymarket-upload.s3.us-east-2.amazonaws.com/which-continent-will-win-the-2026-fifa-world-cup-mMG4porAJtxZ.jpg',
  },
];

// Hyperliquid HIP-4 outcomes carry their underlying in a pipe-delimited
// description (`class:priceBinary|underlying:BTC|expiry:...`). HL itself
// publishes no per-question images, so until they ship one we mirror what
// Polymarket does for crypto-up/down markets and use the coin's logo. For
// non-crypto outcomes (macro prints, politics, etc.) we fall through to
// the topic-pattern table above. Last resort is the Hyperliquid mint glyph.
//
// `slug` and `questionName` are optional so legacy callers keep working,
// but new code should pass them — the topic table reads from the slug/name
// because the description on a CPI question is free-form text, not the
// pipe-delimited `class:…|underlying:…` shape that pricebinary uses.
export function hyperliquidImageFromDescription(
  description?: string | null,
  slug?: string | null,
  questionName?: string | null,
): string {
  const FALLBACK = '/logos/hyperliquid/symbol-mint.svg';
  if (description) {
    const match = description.match(/(?:^|\|)underlying:([^|]+)/i);
    const underlying = match ? match[1].trim().toLowerCase() : '';
    if (underlying && COIN_ICON_URLS[underlying]) return COIN_ICON_URLS[underlying];
  }
  // Topic matching: combine every text signal we have so an editorial
  // rename of the slug ("may-cpi-…" → "us-cpi-may-…") doesn't drop the
  // image. Stops at first match so the patterns table is order-sensitive
  // — narrower patterns first.
  const haystack = `${slug || ''} ${questionName || ''} ${description || ''}`;
  if (haystack.trim()) {
    for (const { pattern, image } of HL_TOPIC_IMAGES) {
      if (pattern.test(haystack)) return image;
    }
  }
  return FALLBACK;
}

/**
 * Transform one HL question (from /api/markets/hyperliquid?grouped=true)
 * into the AggregatedMarket shape consumed by the listing cards. Multi-
 * outcome questions (priceBucket) carry their sub-markets in `q.outcomes`
 * — each one becomes one inner market so the event card renders as a
 * multi-market unit (one card, N rows), matching Polymarket's events.
 *
 * Single-outcome questions still render as a single-binary card; the
 * outcomes array is length 1 and yes/no come from that single inner market.
 *
 * Returns `any` (assignable to AggregatedMarket) so this helper stays
 * decoupled from MarketsPageContent's local AggregatedMarket interface
 * — call sites already tag the result with `: AggregatedMarket[]`.
 */
/**
 * HL stores the full resolution text as `question_name` for some markets
 * (sports 2-way, Fed rate, one-shots), so a naïve title/slug would read as
 * the entire rulebook. This detects that "blob" so callers can fall back to
 * the clean `outcome_name`. Shared by the listing mapper here and the
 * event-detail transform (`hyperliquid/event/[slug]/page.tsx`) — keep the two
 * in sync via this single helper.
 */
export function looksLikeHlBlob(s?: string | null): boolean {
  return !!s && (s.length > 80 || /\bresolves\b|metadata=/i.test(s));
}

export function buildHlEventMarket(q: any): any {
  const outcomes: any[] = Array.isArray(q.outcomes) ? q.outcomes : [];
  // 1X2 football match (Team A / Draw / Team B): HL lists each outcome as an
  // INDEPENDENT binary book, so the three YES midpoints carry overround (they
  // don't sum to 1) and the "Draw" frequently out-prices the underdog team. The
  // grid card (renderMultiMarket) only previews the top-2 by price, which buries
  // the away team under the Draw row — so a "Canada vs Bosnia" card reads
  // "Canada / Draw" with Bosnia hidden. Drop the Draw from the CARD outcomes so
  // both teams surface; the Draw stays fully tradeable on the event detail page
  // (which transforms the question separately, keeping all three outcomes).
  const isMatchWithDraw =
    outcomes.length === 3 &&
    outcomes.some((o: any) => String(o.outcome_name || '').trim().toLowerCase() === 'draw');
  const cardOutcomes = isMatchWithDraw
    ? outcomes.filter((o: any) => String(o.outcome_name || '').trim().toLowerCase() !== 'draw')
    : outcomes;
  const fallbackYesPrice = Math.max(0, Math.min(1, Number(q.yes_price ?? 0.5)));
  const fallbackNoPrice = Math.max(0, Math.min(1, 1 - fallbackYesPrice));
  const yesSlug = q.yes_slug || `q-${q.question_id}`;
  const dailyVol = Number(q.total_volume_usd ?? 0);

  // "Binary" = literally Yes/No. A 2-way outcome with NAMED sides (PSG/Arsenal,
  // Change/No Change) is STILL one binary market: the two sides are the YES
  // (side 0, e.g. PSG) and NO (side 1, e.g. Arsenal) of the SAME outcome, so a
  // trade routes by (outcomeIndex, YES|NO) correctly. We keep it as ONE market
  // and surface the names via `outcomes` so the card's binary buttons read
  // "PSG / Arsenal" instead of "Yes / No" (EventCard.renderBinaryMarket maps
  // `market.outcomes`). Real multi-outcome questions (CPI brackets) keep one
  // market per outcome and stay a multi-market card.
  const isNamedSide = (s?: string | null) => !!s && !/^(yes|no)$/i.test(String(s).trim());
  const innerMarkets = cardOutcomes.length > 0
    ? cardOutcomes.map((o: any) => {
        const ys = o.yes_side_name as string | undefined;
        const ns = o.no_side_name as string | undefined;
        const vol = Number(o.daily_volume_usd ?? 0);
        const yp = Number(o.yes_price ?? 0.5);
        const named = isNamedSide(ys) && isNamedSide(ns);
        return {
          id: String(o.yes_asset_id ?? o.outcome_id),
          ticker: o.yes_slug,
          title: named ? `${ys} / ${ns}` : (o.outcome_name || q.question_name || ''),
          yesPrice: yp,
          noPrice: named ? 1 - yp : Number(o.no_price ?? 1 - yp),
          volume24h: vol,
          // Country outcomes (World Cup teams) carry their flag; null for
          // non-nations so the card falls back to the event image.
          image: getCountryFlagUrl(o.outcome_name) ?? undefined,
          ...(named ? { outcomes: [ys as string, ns as string] } : {}),
        };
      })
    : [{
        id: String(q.yes_asset_id ?? q.question_id),
        ticker: yesSlug,
        title: q.question_name || '',
        yesPrice: fallbackYesPrice,
        noPrice: fallbackNoPrice,
        volume24h: dailyVol,
      }];

  const cardYesPrice = innerMarkets.length === 1 ? innerMarkets[0].yesPrice : fallbackYesPrice;
  const cardNoPrice = innerMarkets.length === 1 ? innerMarkets[0].noPrice : fallbackNoPrice;

  // Clean event title: when question_name is a resolution blob, fall back to
  // the single outcome's clean `outcome_name` ("Champions League Winner").
  // Multi-outcome questions keep question_name (each outcome has its own label).
  const eventTitle = (() => {
    const qn = q.question_name || '';
    if (!looksLikeHlBlob(qn)) return qn;
    const oName = outcomes.length <= 1 ? (outcomes[0]?.outcome_name as string | undefined) : undefined;
    return oName && !looksLikeHlBlob(oName) ? oName : qn;
  })();

  return {
    id: `hl-${q.question_id}-${q.yes_asset_id ?? '0'}`,
    slug: yesSlug,
    title: eventTitle,
    description: q.question_description || undefined,
    // Prefer the server-resolved image (crypto / curated topic / Polymarket
    // auto-match, cached). Fall back to the client pattern helper so a cache
    // miss (display_image null) still renders the glyph instead of nothing.
    image: q.display_image || hyperliquidImageFromDescription(q.question_description, q.yes_slug, q.question_name),
    source: 'hyperliquid',
    yesPrice: cardYesPrice,
    noPrice: cardNoPrice,
    volume: dailyVol,
    volume24h: dailyVol,
    liquidity: 0,
    status: q.status || 'active',
    endDate: null,
    category: 'hyperliquid',
    tags: [],
    marketsCount: innerMarkets.length,
    markets: innerMarkets,
    sourceData: {},
  };
}
